import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import PokemonSprite from "../PokemonSprite";

interface Props { payload: VisualPayload }

const LEAGUE_COLORS: Record<string, string> = {
  great: "text-blue-400",
  ultra: "text-yellow-400",
  master: "text-purple-400",
};

const LEAGUE_DOT: Record<string, string> = {
  great:  "bg-blue-500",
  ultra:  "bg-yellow-500",
  master: "bg-purple-500",
};

// decisions/0016: the verdict word the spoken answer uses, shown beside the rank.
const VERDICT_STYLE: Record<string, string> = {
  "Strong pick":     "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  "Usable":          "bg-sky-500/20 text-sky-300 border-sky-500/30",
  "Not recommended": "bg-white/10 text-white/60 border-white/15",
};

function RankingRow({ sec }: { sec: Record<string, unknown> }) {
  const league = (sec.league as string | undefined)?.toLowerCase() ?? "";
  const leagueColor = LEAGUE_COLORS[league] ?? "text-white";
  const dot = LEAGUE_DOT[league];

  return (
    <div className="rounded-xl border border-white/[0.07] p-3 space-y-3">
      {/* League header */}
      {league && (
        <div className="flex items-center gap-1.5">
          {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
          <p className={`text-xs font-semibold capitalize ${leagueColor}`}>{league} League</p>
        </div>
      )}

      {/* Rank + score + shadow badge */}
      <div className="flex items-end gap-3">
        <span className="text-3xl font-bold text-scout-yellow tabular-nums">#{String(sec.rank)}</span>
        <p className="text-xs text-white/40 pb-0.5">Score {Number(sec.score).toFixed(1)}</p>
        {!!sec.is_shadow && (
          <span className="mb-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none bg-violet-500/20 text-violet-300 border-violet-500/30">
            Shadow
          </span>
        )}
        {typeof sec.verdict === "string" && sec.verdict in VERDICT_STYLE && (
          <span className={`mb-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border leading-none ${VERDICT_STYLE[sec.verdict]}`}>
            {sec.verdict}
          </span>
        )}
      </div>

      {/* Moveset with labels */}
      <div className="bg-white/[0.03] rounded-lg p-2.5 border border-white/[0.05] space-y-2">
        <p className="text-[10px] text-white/30 uppercase tracking-wide">Best Moveset</p>
        <div className="flex items-baseline gap-2">
          <span className="text-[9px] font-semibold uppercase tracking-wide text-scout-yellow/70 w-12 flex-shrink-0">Fast</span>
          <span className="text-sm font-medium text-white">{sec.best_fast_move as string}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[9px] font-semibold uppercase tracking-wide text-blue-400/70 w-12 flex-shrink-0">Charge</span>
          <span className="text-sm text-white/80">{sec.best_charge_move_1 as string}</span>
        </div>
        {!!sec.best_charge_move_2 && (
          <div className="flex items-baseline gap-2">
            <span className="text-[9px] font-semibold uppercase tracking-wide text-blue-400/70 w-12 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm text-white/80">{sec.best_charge_move_2 as string}</span>
          </div>
        )}
      </div>

      {/* PvP counters (decisions/0010) — PvPoke's toughest matchups, worst first */}
      {Array.isArray(sec.pvp_counters) && sec.pvp_counters.length > 0 && (
        <div className="bg-white/[0.03] rounded-lg p-2.5 border border-white/[0.05]">
          <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1">Beaten most by</p>
          <p className="text-sm text-white/80">{(sec.pvp_counters as string[]).join(" · ")}</p>
        </div>
      )}

      {/* IVs row — only when CP data is present */}
      {sec.best_cp != null && (
        <div className="flex items-center justify-between text-sm">
          <div>
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1">Best IVs · Lv{sec.best_level as number}</p>
            <p className="font-mono text-white/80">
              {sec.best_iv_attack as number}/{sec.best_iv_defense as number}/{sec.best_iv_stamina as number}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-white/30 uppercase tracking-wide mb-1">CP</p>
            <p className="font-bold text-white">{(sec.best_cp as number).toLocaleString()}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RankingCard({ payload }: Props) {
  const sections = payload.sections.filter((s) => s.section === "ranking");

  // One card = one Pokémon across (possibly) multiple leagues — pull the
  // sprite info from the first section.  All sections share the same
  // pokedex_id since the backend assembles per-species.  Falling back to
  // the payload title for the alt text since the section dict doesn't
  // carry the species name directly.
  const firstSec = sections[0] as Record<string, unknown> | undefined;
  const pokedexId = firstSec?.pokedex_id as number | undefined;

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {sections.length > 0 ? (
        <>
          {/* Species header — one sprite for the whole card, since each
              league row underneath is just a different ranking slice of
              the same Pokémon.  Keeps the card visually anchored without
              repeating the sprite N times. */}
          {pokedexId != null && (
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-white/[0.06]">
              <PokemonSprite
                pokedexId={pokedexId}
                alt={payload.title}
                size="lg"
              />
              <p className="text-sm font-semibold text-white/85 leading-tight">{payload.title}</p>
            </div>
          )}
          <div className="space-y-3">
            {sections.map((sec, i) => (
              <RankingRow key={(sec.league as string | undefined) ?? String(i)} sec={sec as Record<string, unknown>} />
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-white/40">No ranking data.</p>
      )}
    </CardShell>
  );
}
